import React, { useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import pkg from "../../package.json";
const { version } = pkg;

const CONTACT_EMAIL = "email@artasupport.ai";
const LAST_UPDATED = "October 5, 2026";

// The policies describe the Arta-hosted tenant, and one frontend build serves
// every tenant, so the links are shown by hostname.
const LEGAL_LINK_HOSTNAMES = ["zscaler.artasupport.com", "localhost", "127.0.0.1"];
export const showLegalLinks =
  typeof window !== "undefined" && LEGAL_LINK_HOSTNAMES.includes(window.location.hostname);

const Mail = () => (
  <a href={`mailto:${CONTACT_EMAIL}`} className="text-cyan-400 hover:text-cyan-300 underline underline-offset-2">
    {CONTACT_EMAIL}
  </a>
);

const Section = ({ title, children }) => (
  <section className="mb-10">
    <h2 className="text-xl font-semibold text-white mb-3">{title}</h2>
    <div className="space-y-3 text-white/75 leading-relaxed">{children}</div>
  </section>
);

const List = ({ items }) => (
  <ul className="list-disc pl-6 space-y-1.5">
    {items.map((item, i) => (
      <li key={i}>{item}</li>
    ))}
  </ul>
);

export const LegalFooterLinks = ({ className = "" }) => (
  <span className={className}>
    <Link to="/privacy" className="hover:text-cyan-400 transition underline underline-offset-2">Privacy Policy</Link>
    {" · "}
    <Link to="/terms" className="hover:text-cyan-400 transition underline underline-offset-2">Terms of Service</Link>
  </span>
);

const LegalLayout = ({ title, children }) => {
  const navigate = useNavigate();

  useEffect(() => {
    const originalBg = document.body.style.backgroundColor;
    document.body.style.backgroundColor = "#0d1f26";
    window.scrollTo(0, 0);
    return () => {
      document.body.style.backgroundColor = originalBg;
    };
  }, []);

  return (
    <div className="min-h-screen bg-[#0d1f26] text-white font-sans">
      <nav className="sticky top-0 z-50 bg-[#0d1f26]/95 backdrop-blur border-b border-white/10 px-6 py-3 flex items-center justify-between">
        <Link to="/about" className="flex items-center gap-3">
          <img
            src="/A_bgless.png"
            alt="Arta Logo"
            className="h-9 w-auto"
            onError={(e) => { e.target.onerror = null; e.target.src = "/A.png"; }}
          />
          <span className="text-white font-semibold text-lg tracking-wide">Arta</span>
        </Link>
        <button
          onClick={() => (window.history.length > 1 ? navigate(-1) : navigate("/"))}
          className="bg-cyan-500 hover:bg-cyan-600 text-white text-sm px-4 py-1.5 rounded-lg shadow transition"
        >
          ← Back
        </button>
      </nav>

      <main className="max-w-3xl mx-auto px-6 py-16">
        <h1 className="text-4xl font-bold text-white mb-2">{title}</h1>
        <p className="text-white/50 text-sm mb-12">Last updated: {LAST_UPDATED}</p>
        {children}
      </main>

      <footer className="py-10 px-6 bg-[#0d1f26] border-t border-white/10 text-center">
        <p className="text-white/40 text-sm">
          © {new Date().getFullYear()} Arta Inc. · San Jose, CA ·{" "}
          <a href={`mailto:${CONTACT_EMAIL}`} className="text-cyan-500 hover:text-cyan-400 transition">
            {CONTACT_EMAIL}
          </a>
        </p>
        <p className="text-white/40 text-sm mt-2">
          <Link to="/about" className="hover:text-cyan-400 transition underline underline-offset-2">About</Link>
          {" · "}
          <LegalFooterLinks />
        </p>
        <p className="text-white/20 text-xs mt-1">v{version}</p>
      </footer>
    </div>
  );
};

export const PrivacyPolicy = () => (
  <LegalLayout title="Privacy Policy">
    <Section title="Overview">
      <p>
        This Privacy Policy explains how Arta Inc. ("Arta", "we", "us") collects, uses and protects
        information when you use the Arta AI technical-support platform, including our web application
        and customer tenants hosted on artasupport.com and artasupport.ai (the "Service").
      </p>
      <p>
        Arta is a business-to-business service. Our users are support engineers and staff at our company
        and at our enterprise customers.
      </p>
    </Section>

    <Section title="Information we collect">
      <List
        items={[
          <><strong className="text-white">Account information:</strong> your username, email address, the account type you request, and your password (stored only by our authentication provider, never in plain text).</>,
          <><strong className="text-white">Content you provide:</strong> questions, messages, case details, files and logs that you submit to the Service so it can help troubleshoot technical issues.</>,
          <><strong className="text-white">Usage and technical data:</strong> sign-in times, actions taken in the Service, IP address, browser type, and service logs used to operate, secure and improve the Service.</>,
        ]}
      />
    </Section>

    <Section title="How we use information">
      <List
        items={[
          "To create and manage your account, including verifying your email address and reviewing account requests.",
          "To keep your account secure, including sending one-time verification codes when you sign up and when you sign in.",
          "To send service emails about your account, such as account approval, welcome and password reset messages.",
          "To provide the Service, including generating AI-assisted troubleshooting answers from the content you submit.",
          "To monitor, maintain, secure and improve the Service.",
        ]}
      />
      <p>
        We send only transactional emails related to your account and its security. We do not send
        marketing email, and we do not sell or rent your personal information.
      </p>
    </Section>

    <Section title="Service providers">
      <p>We use trusted service providers to operate the Service, who process information on our behalf:</p>
      <List
        items={[
          "Amazon Web Services (AWS) for hosting, user authentication (Amazon Cognito), data storage and email delivery (Amazon SES).",
          "AI model and search providers (for example OpenAI and Anthropic) to generate responses to the content you submit.",
        ]}
      />
      <p>
        These providers may only use the information to provide their services to us. We may also disclose
        information if required by law or to protect the rights and safety of our users and the Service.
      </p>
    </Section>

    <Section title="Data retention">
      <p>
        We keep account information for as long as your account is active. Content and logs are retained as
        needed to provide the Service and as agreed with our enterprise customers. Sign-up requests that are
        never verified or are rejected are deleted. When an account is deleted, we remove its account
        information, except where we must keep certain records to meet legal obligations.
      </p>
    </Section>

    <Section title="Security">
      <p>
        We protect information with access controls, encryption in transit, email verification of new
        accounts, administrator approval of account requests, and one-time sign-in codes. No method of
        transmission or storage is completely secure, but we work to protect your information.
      </p>
    </Section>

    <Section title="Your choices and rights">
      <p>
        You can ask us to access, correct or delete your personal information, or to close your account,
        by contacting us at <Mail />. Because our emails are account and security messages, they stop when
        your account is closed. If your account was provided by your employer, we may need to coordinate
        your request with them.
      </p>
    </Section>

    <Section title="Children">
      <p>The Service is intended for business use and is not directed to children under 16.</p>
    </Section>

    <Section title="Changes to this policy">
      <p>
        We may update this Privacy Policy from time to time. We will post the updated version on this page
        and change the "Last updated" date above.
      </p>
    </Section>

    <Section title="Contact us">
      <p>
        Arta Inc., San Jose, CA. Email: <Mail />
      </p>
    </Section>
  </LegalLayout>
);

export const TermsOfService = () => (
  <LegalLayout title="Terms of Service">
    <Section title="Agreement">
      <p>
        These Terms of Service ("Terms") govern your use of the Arta AI technical-support platform provided by
        Arta Inc. ("Arta", "we", "us"), including our web application and customer tenants hosted on
        artasupport.com and artasupport.ai (the "Service"). By creating an account or using the Service, you
        agree to these Terms. If you use the Service on behalf of an organization, you agree to them on that
        organization's behalf. Where your organization has a separate written agreement with Arta, that
        agreement takes precedence.
      </p>
    </Section>

    <Section title="Accounts">
      <List
        items={[
          "You must provide a valid email address that you control. We verify it with a one-time code before your request is reviewed.",
          "Account requests are reviewed by an administrator, who may approve or reject them.",
          "You are responsible for keeping your password and sign-in codes confidential and for activity under your account.",
          "Tell us right away at the contact address below if you suspect unauthorized use of your account.",
        ]}
      />
    </Section>

    <Section title="Acceptable use">
      <p>You agree not to:</p>
      <List
        items={[
          "Use the Service for anything unlawful, harmful or fraudulent.",
          "Submit content you do not have the right to share, or content that infringes others' rights.",
          "Attempt to gain unauthorized access to the Service, other accounts, or related systems.",
          "Interfere with or disrupt the Service, or probe, scan or test its vulnerabilities without permission.",
          "Reverse engineer the Service or use it to build a competing product.",
          "Share your account or sign-in codes with others.",
        ]}
      />
    </Section>

    <Section title="Your content">
      <p>
        You keep ownership of the content you submit. You give Arta permission to process that content to
        provide, secure and support the Service, as described in our{" "}
        <Link to="/privacy" className="text-cyan-400 hover:text-cyan-300 underline underline-offset-2">Privacy Policy</Link>.
      </p>
    </Section>

    <Section title="AI-generated output">
      <p>
        The Service uses artificial intelligence to suggest troubleshooting steps and answers. AI output may
        be incomplete or incorrect. You are responsible for reviewing it before relying on it or sharing it
        with others.
      </p>
    </Section>

    <Section title="Service emails">
      <p>
        By creating an account you agree to receive transactional emails needed to operate your account, such
        as verification codes, sign-in codes, account approval and password reset messages. These stop when
        your account is closed.
      </p>
    </Section>

    <Section title="Suspension and termination">
      <p>
        We may suspend or close accounts that violate these Terms or put the Service or other users at risk.
        You may stop using the Service and ask us to close your account at any time.
      </p>
    </Section>

    <Section title="Disclaimers">
      <p>
        The Service is provided "as is" and "as available", without warranties of any kind, to the extent
        permitted by law.
      </p>
    </Section>

    <Section title="Limitation of liability">
      <p>
        To the extent permitted by law, Arta will not be liable for any indirect, incidental, special,
        consequential or punitive damages, or for any loss of data, profits or revenue, arising from your use
        of the Service.
      </p>
    </Section>

    <Section title="Changes to these Terms">
      <p>
        We may update these Terms from time to time. We will post the updated version on this page and change
        the "Last updated" date above. Continuing to use the Service after a change means you accept the
        updated Terms.
      </p>
    </Section>

    <Section title="Governing law">
      <p>These Terms are governed by the laws of the State of California, USA.</p>
    </Section>

    <Section title="Contact us">
      <p>
        Arta Inc., San Jose, CA. Email: <Mail />
      </p>
    </Section>
  </LegalLayout>
);
